import { ArrowUpRight, MapPin, Instagram } from "lucide-react";
import { CONTACT, BRAND } from "../../data/collection";
import { IG_DM, buildGeneralMessage, waLink } from "../../lib/order";
import { WhatsAppMark, ReplyNudge } from "./QuickChat";
import { Reveal } from "./Reveal";

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact"
      className="relative bg-[#FDFBF7] text-[#050505] border-t border-black/10"
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20 py-24 md:py-32">
        <div className="grid gap-16 md:grid-cols-12">
          {/* reach us */}
          <div className="md:col-span-5">
            <Reveal>
              <p className="font-body text-[11px] uppercase tracking-[0.4em] text-black/40">
                Say hello
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-display text-5xl md:text-6xl font-light leading-[0.95]">
                Get in
                <br />
                <span className="font-serif-editorial italic text-black/70">touch.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-sm font-body text-sm font-light leading-relaxed text-black/55">
                Every order happens over a friendly chat. Tell us the piece and
                your size — we&apos;ll take care of the rest.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  data-testid="contact-order-whatsapp"
                  href={waLink(buildGeneralMessage())}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 rounded-full bg-[#25D366] px-7 py-4 font-body text-[12px] uppercase tracking-[0.24em] text-white transition-transform duration-300 hover:brightness-110 hover:translate-y-[-1px]"
                >
                  <WhatsAppMark className="h-4 w-4" />
                  WhatsApp us
                </a>
                <a
                  data-testid="contact-order-dm"
                  href={IG_DM}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 rounded-full bg-[#050505] px-7 py-4 font-body text-[12px] uppercase tracking-[0.24em] text-white transition-colors duration-300 hover:bg-[var(--kln-gold)] hover:text-black"
                >
                  Instagram DM
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
                </a>
              </div>
              <ReplyNudge testid="contact-wa-nudge" className="mt-3" />
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
                <a
                  data-testid="contact-whatsapp-number"
                  href={waLink(buildGeneralMessage())}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.24em] text-black/60 transition-colors hover:text-black"
                >
                  <WhatsAppMark className="h-4 w-4 text-[#25D366]" />
                  {BRAND.whatsappNumber}
                </a>
                <a
                  data-testid="contact-instagram"
                  href={BRAND.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.24em] text-black/60 transition-colors hover:text-black"
                >
                  <Instagram className="h-4 w-4" strokeWidth={1.3} />
                  {BRAND.instagramHandle}
                </a>
              </div>
            </Reveal>
          </div>

          {/* locations */}
          <div className="md:col-span-7">
            <div className="grid gap-10 sm:grid-cols-2">
              {/* production unit */}
              <Reveal delay={0.1}>
                <div className="border-t border-black/15 pt-6">
                  <p className="font-body text-[10px] uppercase tracking-[0.28em] text-black/40">
                    {CONTACT.unit.label}
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[var(--kln-gold)]" strokeWidth={1.4} />
                    <h4 className="font-display text-xl tracking-wide">{CONTACT.unit.city}</h4>
                  </div>
                  <address className="mt-3 not-italic font-body text-sm font-light leading-relaxed text-black/55">
                    {CONTACT.unit.lines.map((l, i) => (
                      <span key={i} className="block">{l}</span>
                    ))}
                  </address>
                </div>
              </Reveal>

              {/* stockist store */}
              <Reveal delay={0.16}>
                <div className="border-t border-black/15 pt-6">
                  <p className="font-body text-[10px] uppercase tracking-[0.28em] text-black/40">
                    {CONTACT.store.label}
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[var(--kln-gold)]" strokeWidth={1.4} />
                    <h4 className="font-serif-editorial text-xl italic">{CONTACT.store.name}</h4>
                  </div>
                  <address className="mt-3 not-italic font-body text-sm font-light leading-relaxed text-black/55">
                    {CONTACT.store.lines.map((l, i) => (
                      <span key={i} className="block">{l}</span>
                    ))}
                  </address>
                  <a
                    data-testid="contact-stockist-instagram"
                    href={CONTACT.store.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group mt-4 inline-flex items-center gap-1.5 font-body text-[10px] uppercase tracking-[0.22em] text-black/50 transition-colors hover:text-black"
                  >
                    {CONTACT.store.instagramHandle}
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.4} />
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
